import { t as translate } from "../preferences";
import { AlertTriangle, Inbox, LoaderCircle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type DependencyList, type ReactNode } from "react";
import { ApiError } from "../api";
import { useAccessRevision, useReadsAllowed } from "../auth";
import { locale } from "../preferences";

export function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : translate("Ma’lumotni yuklab bo‘lmadi");
}

export function useResource<T>(load: () => Promise<T>, dependencies: DependencyList) {
  const revision = useAccessRevision();
  const allowed = useReadsAllowed();
  const allowedRef = useRef(allowed); allowedRef.current = allowed;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const loader = useRef(load);
  loader.current = load;

  const retry = useCallback(async () => {
    if (!allowedRef.current) return;
    const current = ++sequence.current;
    setLoading(true);
    setError(null);
    try {
      const next = await loader.current();
      if (current === sequence.current) setData(next);
    } catch (caught) {
      if (current === sequence.current) setError(messageOf(caught));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void retry();
    return () => { ++sequence.current; };
    // Dependencies intentionally define the resource identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry, revision, allowed, ...dependencies]);

  return { data, loading, error, retry, setData };
}

export function InitialState({
  loading, error, empty, onRetry, children,
}: {
  loading: boolean; error: string | null; empty?: boolean;
  onRetry: () => void; children: ReactNode;
}) {
  if (loading && !children) {
    return <div className="m-flex m-min-h-40 m-items-center m-justify-center" aria-label={translate("Yuklanmoqda")}><LoaderCircle className="m-size-7 m-animate-spin m-text-primary" /></div>;
  }
  if (error && !children) {
    return <StateCard icon={<AlertTriangle />} title={translate("Yuklab bo‘lmadi")} text={error} action={<button className="m-primary-button m-mt-4" onClick={onRetry}><RefreshCw className="m-size-5" />{translate("Qayta urinish")}</button>} />;
  }
  if (empty) return <StateCard icon={<Inbox />} title={translate("Hozircha ma’lumot yo‘q")} text={translate("Yangi ma’lumot paydo bo‘lganda shu yerda ko‘rinadi.")} />;
  return <>{children}</>;
}

export function InlineError({ message, onRetry, viewport = false }: { message: string | null; onRetry?: () => void; viewport?: boolean }) {
  if (!message) return null;
  const alert = <div className={`m-error-box m-mb-4 m-flex m-items-center m-gap-2${viewport ? " m-whitespace-pre-line" : ""}`} role="alert"><span className="m-min-w-0 m-flex-1">{message}</span>{onRetry ? <button className="m-min-h-11 m-shrink-0 m-rounded-xl m-px-3 m-font-bold" onClick={onRetry}>{translate("Qayta")}</button> : null}</div>;
  return viewport ? <div className="m-viewport-error">{alert}</div> : alert;
}

export function StateCard({ icon, title, text, action }: { icon: ReactNode; title: string; text: string; action?: ReactNode }) {
  return <section className="m-mx-auto m-mt-8 m-w-full m-max-w-sm m-rounded-app m-border m-border-border m-bg-surface m-p-6 m-text-center m-shadow-card">
    <span className="m-mx-auto m-flex m-size-14 m-items-center m-justify-center m-rounded-2xl m-bg-elevated m-text-primary">{icon}</span>
    <h1 className="m-mt-4 m-text-xl m-font-bold">{title}</h1><p className="m-mt-2 m-text-sm m-leading-6 m-text-muted">{text}</p>{action}
  </section>;
}

export function money(value: string | number) {
  return `${Number(value || 0).toLocaleString(locale())} ${translate("so‘m")}`;
}

export function dateTime(value: string) {
  return new Intl.DateTimeFormat(locale(), { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function makeUuid() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
